import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/staff-management';

const StaffManagementLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Staff Management & HR Hub | Explanatum';
    const desc = 'Manage staff records, time tracking, performance reviews, leave accruals, and org charts—without BambooHR or Gusto per-employee pricing.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Staff management overview" className="relative overflow-hidden">
        <div className="absolute top-20 right-20 w-64 h-64 bg-emerald-50 rounded-full mix-blend-multiply filter blur-3xl opacity-40" aria-hidden="true" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-green-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1 text-sm font-semibold text-emerald-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-600" />
                AI Staff Management & HR Hub
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Manage your team without per-employee pricing.
                <span className="mt-2 block bg-gradient-to-r from-emerald-600 to-green-700 bg-clip-text text-transparent">
                  Complete HR platform + 40+ business tools in one subscription.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Staff records, time tracking, performance reviews, leave accruals, org charts, and onboarding workflows. One fixed price includes Staff Management plus all Explanatum features—no per-employee fees, no hidden costs.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_staff_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start 7-day free trial
                </a>
                <a
                  href="/staff-management"
                  className="inline-flex items-center justify-center rounded-full border-2 border-emerald-600 px-6 py-3 text-base font-semibold text-emerald-600 hover:bg-emerald-50 transition-colors"
                >
                  View features
                </a>
              </div>
              <p className="mt-4 text-sm text-gray-500">
                ✨ Includes all Explanatum features: Staff Hub + AI Consultant + Cold Email + CRM + 40+ modules
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Auto leave accruals & org charts
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-600" />
                  Time tracking with GPS location
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-600" />
                  Performance reviews & onboarding
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="HR dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-emerald-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-green-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Team snapshot</span>
                  <span>Live overview</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">👔 45 active staff • 3 on leave • 2 pending holiday requests • 8 reviews due this month</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">⏰ 428 hours logged this week • 12 staff clocked in • 3 pending timesheet approvals</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">📊 Sarah Johnson: 18.5 days PTO remaining • Auto-accrued 1.75 days this month • Next review: Jan 15</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Enterprise HR features at startup pricing</h2>
            <p className="mt-4 text-base text-gray-500">92% feature parity with BambooHR at 85% lower cost—plus integrated business tools they don't have.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: '🤖 Auto Leave Accruals',
              body: 'Automatic PTO calculation based on tenure, hire date, and policy rules. Supports monthly, biweekly, annual, and per-hour accruals with caps, carryovers, and pro-rating for new hires.'
            }, {
              title: '⏰ Time Clock & Attendance',
              body: 'GPS location tracking, clock in/out with notes, break duration logging, timesheet approvals, and overtime calculations—all with mobile support for remote teams.'
            }, {
              title: '👔 Staff Records & Org Charts',
              body: 'Complete employee database with emergency contacts, documents, salary history, and visual org charts with 3 view modes (hierarchy, department, list).'
            }, {
              title: '📊 Performance Reviews',
              body: 'Annual, probation, mid-year, and 360° reviews with customizable templates, goal tracking, training needs assessment, and manager comments.'
            }, {
              title: '📅 Leave Management',
              body: 'Holiday requests with approval workflows, conflict detection, team coverage planning, and automatic balance deductions when approved.'
            }, {
              title: '🎯 Onboarding Workflows',
              body: 'Customizable onboarding templates with task checklists, equipment requests, compliance requirements, and progress tracking for new hires.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why businesses switch from BambooHR and Gusto</h2>
            <p className="mt-4 text-base text-gray-500">Get enterprise HR features without per-employee pricing, payroll complexity, or separate subscriptions.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum (All Features Included)',
              points: [
                '🤖 Auto leave accruals with tenure tiers (UNIQUE!)',
                'Time tracking with GPS & mobile support',
                'Org charts with 3 view modes',
                'Performance reviews & onboarding workflows',
                'Complete staff records & documents',
                'Self-service portal for employees',
                'Fixed: £49/mo (unlimited staff) includes HR + 40+ modules'
              ],
              accent: 'border-emerald-200 bg-emerald-50 text-gray-900'
            }, {
              title: 'BambooHR',
              points: [
                'Per-employee pricing: $8-12/user/month',
                'Accrual tracking requires manual setup',
                'ATS features add $6-8/employee',
                'E-signatures cost extra',
                'Mobile app included but limited',
                'No CRM or project management',
                'Typical cost for 20 employees: $200/month'
              ],
              accent: 'border-gray-200 bg-white text-gray-900'
            }, {
              title: 'Gusto',
              points: [
                'Base $40 + $6 per employee/month',
                'Primarily payroll-focused',
                'Limited performance review features',
                'No org chart visualization',
                'Time tracking requires Premium tier',
                'No project management tools',
                'Typical cost for 20 employees: $160/month (payroll only)'
              ],
              accent: 'border-gray-200 bg-white text-gray-900'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-gray-900" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center">
            <h3 className="text-2xl font-semibold text-gray-900">"Ready to replace BambooHR and Gusto?"</h3>
            <p className="mt-3 text-base text-gray-600">Get complete HR features + integrated CRM, project management, and 40+ business modules for one flat monthly fee. No per-employee charges, no surprise costs, no compromises.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_staff_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-emerald-600 hover:bg-emerald-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg hover:scale-[1.02]"
              >
                Start 7-day free trial
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              💳 No credit card required • 🎁 Full access to all features • ⚡ Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-gray-50 py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where professional HR management drives growth</h2>
            <p className="mt-4 text-base text-gray-500">Give HR, operations, and leadership teams one unified platform for all people management needs.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Growing startups (5-50 employees)',
              body: 'Manage staff records, track time, handle leave requests, and conduct reviews without BambooHR per-employee costs as you scale.'
            }, {
              title: 'Small businesses with remote teams',
              body: 'GPS time tracking, self-service portals, digital document storage, and mobile access for distributed workforces.'
            }, {
              title: 'Service companies tracking hours',
              body: 'Clock in/out for field staff, project time allocation, timesheet approvals, and payroll-ready reports for billing.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white/60 p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Differentiation */}
      <section aria-label="Advanced features" className="relative overflow-hidden bg-white py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.18),transparent_55%)]" aria-hidden="true" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Advanced HR automation no competitor offers at this price</h2>
            <p className="mt-4 text-base text-gray-500">3 unique capabilities that save 10+ hours per month in HR admin—included at no extra cost.</p>
          </div>
          <div className="mt-12 space-y-8">
            <article className="rounded-3xl border border-emerald-400/30 bg-emerald-400/10 p-8 ">
              <div className="flex items-start gap-4">
                <span className="text-4xl">🤖</span>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">Intelligent Leave Accrual Engine (UNIQUE!)</h3>
                  <p className="mt-3 text-base text-gray-600">Automatic leave calculation that adapts to your policies:</p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-500">
                    <li><strong className="text-gray-900">• Tenure-Based Tiers:</strong> Automatically increase accrual rates after 1, 5, 10 years of service</li>
                    <li><strong className="text-gray-900">• Multiple Frequencies:</strong> Monthly, biweekly, annually, or per-hour worked accruals</li>
                    <li><strong className="text-gray-900">• Smart Pro-Rating:</strong> Automatically adjusts for mid-year hires and part-time schedules</li>
                    <li><strong className="text-gray-900">• Carryover Management:</strong> Set caps, expiry dates, and automatic year-end rollovers</li>
                    <li><strong className="text-gray-900">• Real-Time Balances:</strong> Always accurate PTO totals with full audit history</li>
                  </ul>
                  <p className="mt-4 text-sm text-emerald-700 font-medium">Saves 5-10 hours per month vs manual spreadsheets. BambooHR requires manual policy setup.</p>
                </div>
              </div>
            </article>

            <article className="rounded-3xl border border-blue-400/30 bg-blue-400/10 p-8 ">
              <div className="flex items-start gap-4">
                <span className="text-4xl">👔</span>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">Visual Org Charts with 3 View Modes</h3>
                  <p className="mt-3 text-base text-gray-600">See your team structure at a glance with interactive visualizations:</p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-500">
                    <li><strong className="text-gray-900">• Hierarchy View:</strong> Visual tree showing reporting relationships and team structures</li>
                    <li><strong className="text-gray-900">• Department View:</strong> Group staff by department with team sizes and managers</li>
                    <li><strong className="text-gray-900">• List View:</strong> Sortable table with all staff details and direct reports count</li>
                    <li><strong className="text-gray-900">• Smart Search:</strong> Filter by name, position, or department instantly</li>
                  </ul>
                  <p className="mt-4 text-sm text-blue-700 font-medium">Gusto doesn't offer org chart visualization. BambooHR charges extra for this feature.</p>
                </div>
              </div>
            </article>

            <article className="rounded-3xl border border-purple-400/30 bg-purple-400/10 p-8 ">
              <div className="flex items-start gap-4">
                <span className="text-4xl">💎</span>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900">One Subscription = Complete Business Platform</h3>
                  <p className="mt-3 text-base text-gray-600">Your monthly fee includes Staff Management PLUS all Explanatum features:</p>
                  <ul className="mt-4 space-y-2 text-sm text-gray-500">
                    <li><strong className="text-gray-900">• AI Business Consultant:</strong> Strategic HR advice integrated with your staff data</li>
                    <li><strong className="text-gray-900">• Project Management:</strong> Track team workload and capacity planning</li>
                    <li><strong className="text-gray-900">• CRM & Pipeline Management:</strong> Connect HR data with sales and customer success</li>
                    <li><strong className="text-gray-900">• 40+ Business Modules:</strong> Finance, marketing, operations, and analytics tools</li>
                  </ul>
                  <p className="mt-4 text-sm text-purple-700 font-medium">Replace 5-10 separate subscriptions with one unified platform. Save $1,500+/month vs BambooHR + other tools.</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default StaffManagementLanding;
